import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../service/caller';

// Importation des images
import medic from '../../assets/images/Medic.png';
import paypal from '../../assets/images/paypal.png';
import mtn from '../../assets/images/mtn.png';
import bank from '../../assets/images/bank.png';

const UpdateSubscription = () => {
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [features, setFeatures] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [compareMode, setCompareMode] = useState(false);
  const [currentPlanId, setCurrentPlanId] = useState();
  const [userData, setUserData] = useState();
  const [expirationDate, setExpirationDate] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  
  // État pour le plan personnalisé
  const [showCustomPlan, setShowCustomPlan] = useState(false);
  const [customPlan, setCustomPlan] = useState({
    hospitalId: "",
    features: [],
    planDuration: 1,
    planPrice: 0,
    isPaid: false
  });

  const calculateTotalPrice = () => {
    let totalPrice = 0;
  
    if (Array.isArray(customPlan?.features)) {
      customPlan.features.forEach(featureSelection => {
        const feature = features.find(f => f._id === featureSelection.featureId);
        if (feature && featureSelection.quantity > 0) {
          totalPrice += feature.price * featureSelection.quantity;
        }
      });
    }
  
    // Ajuster en fonction de la durée (en jours)
    // Calcul: prix x durée en mois (durée en jours / 30)
    const durationFactor = customPlan.planDuration;
    totalPrice *= durationFactor;
  
    return Math.round(totalPrice);
  };
  
  

  // Récupération des plans et du plan actuel
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const userRes = await api.get('api/usersprofile');
        const userData = userRes.data;
        setUserData(userData);
        
        // Récupérer les informations de l'abonnement
        const subscriptionRes = await api.get(
          `api/adminsubscriptions/${userData?.main_hospital_id}`
        );
        const subscriptionData = subscriptionRes.data;
        setCurrentPlanId(subscriptionData.subscription._id);
        
        // Récupérer le plan actuel
        const currentPlanResponse = await api.get(`/api/adminsubscriptions/${userData?.main_hospital_id}`);
        const currentPlanData = currentPlanResponse.data.subscription;
        setCurrentPlan(currentPlanData);
        
        // Calculer la date d'expiration basée sur la date de création et la durée du plan
        if (currentPlanData && currentPlanResponse.data.hospital.subscriptAt) {
          const creationDate = new Date(currentPlanResponse.data.hospital.subscriptAt);
          const planDuration = currentPlanData.planDuration || currentPlanData.duration || 1; // Durée en mois
          
          // Calculer la date d'expiration (création + durée en mois)
          const expDate = new Date(creationDate);
          expDate.setMonth(expDate.getMonth() + planDuration);
          setExpirationDate(expDate);
          
          // Vérifier si le plan a expiré
          const today = new Date();
          setIsExpired(today > expDate);
        }
        
        // Récupérer tous les plans disponibles
        const plansResponse = await api.get('/api/subscriptions');
        setPlans(plansResponse.data);
        
        // Récupérer toutes les fonctionnalités
        const featuresResponse = await api.get('/api/getallfeatures');
        setFeatures(featuresResponse.data);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };
  
    fetchData();
  }, [currentPlanId]);

  const renewCurrentPlan = async () => {
    try {
      // Déterminez si le plan actuel est personnalisé ou standard
      const isCustomPlan = !currentPlan.name || currentPlan.name === "Plan personnalisé"; 
      
      // Préparez les données de paiement
      const planName = isCustomPlan ? "Plan personnalisé" : currentPlan.name;
      const planPrice = currentPlan.price || currentPlan.planPrice;
      const planDuration = currentPlan.planDuration || currentPlan.duration || 1;
      
      // Configurez les données pour le paiement en fonction de la méthode choisie
      if (paymentMethod === 'paypal') {
        const paymentData = {
          item_name: `Renouvellement de ${planName}`,
          price: planPrice,
          currency: 'USD',
          paymentReason: 'Subscription Renewal for hospital',
          motifId: 'Subscription Renewal',
        };
  
        const paymentResponse = await api.post(`/payment/pay`, paymentData);
  
        if (paymentResponse.data.approval_url) {
          window.open(paymentResponse.data.approval_url, '_blank');
        } else {
          toast.error('Erreur: Impossible de générer le lien de paiement');
        }
      } else if (paymentMethod === 'mtnmomo') {
        if (!phoneNumber) {
          toast.warning('Veuillez saisir un numéro de téléphone');
          return;
        }
        
        const paymentData = {
          item_name: `Renouvellement de ${planName}`,
          amount: planPrice,
          currency: 'USD',
          phoneNumber: phoneNumber,
          payerMessage: 'Renouvellement d\'abonnement',
          paymentReason: 'Subscription Renewal for hospital',
          motifId: 'Subscription Renewal',
        };
  
        const paymentResponse = await api.post(`/payment/paymentmomo/initiate`, paymentData);
        
        if (paymentResponse?.status === 200) {
          toast.success("Paiement réussi! Votre abonnement a été renouvelé.");
          
          // Si c'est un plan personnalisé, recréez-le
          if (isCustomPlan) {
            // Préparer les données pour le renouvellement du plan personnalisé
            const personalizedPlanData = {
              hospitalId: userData.main_hospital_id,
              features: Array.isArray(currentPlan.features) 
                ? currentPlan.features.map(f => ({
                    featureId: f.featureId?._id || f.featureId || f.feature?._id || f.feature,
                    quantity: f.quantity || (f.value === true ? 1 : 0)
                  })).filter(f => f.quantity > 0)
                : [],
              planDuration: planDuration,
              planPrice: planPrice,
              isPaid: true
            };
            
            // Créer un nouveau plan personnalisé basé sur l'actuel
            await api.post('/api/customize-subscription', personalizedPlanData);
          } else {
            // Sinon, mettez simplement à jour avec le même plan standard
            await api.put(`/api/updatehospital/${userData.hospital_id[0]._id}`, {
              subscription_id: currentPlan._id
            });
          }
          
          // Redirection vers le tableau de bord après renouvellement
          setTimeout(() => {
            window.location.href = '/hospitaladmin/dhashboard';
          }, 1500);
        } else {
          toast.error("Échec du paiement");
        }
      } else {
        toast.warning('Veuillez sélectionner une méthode de paiement');
      }
    } catch (error) {
      console.error('Erreur lors du renouvellement:', error);
      toast.error(`Échec du renouvellement: ${error?.message}`);
    }
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setCompareMode(true);
    setShowCustomPlan(false);
  };

  const handleSelectPaymentMethod = (e) => {
    setPaymentMethod(e.target.value);
  };

  const handlePhoneNumberChange = (e) => {
    setPhoneNumber(e.target.value);
  };

  const calculatePriceDifference = () => {
    if (!currentPlan || (!selectedPlan && !showCustomPlan)) return 0;
    
    // Pour le plan personnalisé, on utilise son prix directement
    const newPlanPrice = showCustomPlan ? calculateTotalPrice() : selectedPlan.price;
    
    // Calcul de la différence de prix (prorata)
    const remainingDays = calculateRemainingDays();
    const currentDailyRate = currentPlan.price / (currentPlan.duration * 30);
    const remainingValue = currentDailyRate * remainingDays;
    
    return newPlanPrice;
  };

  const calculateRemainingDays = () => {
    // Cette fonction devrait calculer les jours restants dans l'abonnement actuel
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 30);
    
    const today = new Date();
    const diffTime = expirationDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  };

  const handlePaypalPayment = async () => {
    try {
      const planName = showCustomPlan ? customPlan.name : selectedPlan.name;
      
      const paymentData = {
        item_name: `Upgrade to ${planName}`,
        price: calculatePriceDifference(),
        currency: 'USD',
        paymentReason: 'Subscription Upgrade for hospital',
        motifId: 'Subscription Upgrade',
      };

      const paymentResponse = await api.post(`/payment/pay`, paymentData);

      if (paymentResponse.data.approval_url) {
        window.open(paymentResponse.data.approval_url, '_blank');
      } else {
        toast.error('Erreur: Impossible de générer le lien de paiement');
      }
    } catch (error) {
      console.error('Erreur lors du paiement:', error);
      toast.error(`Erreur de paiement: ${error.message}`);
    }
  };

  const handleMomoPayment = async () => {
    try {
      if (!phoneNumber) {
        toast.warning('Veuillez saisir un numéro de téléphone');
        return;
      }

      const planName = showCustomPlan ? "Plan personnalisé" : selectedPlan.name;
      
      const paymentData = {
        item_name: `Upgrade to ${planName}`,
        amount: calculatePriceDifference(),
        currency: 'USD',
        phoneNumber: phoneNumber,
        payerMessage: 'Mise à jour d\'abonnement',
        paymentReason: 'Subscription Upgrade for hospital',
        motifId: 'Subscription Upgrade',
      };

      const paymentResponse = await api.post(`/payment/paymentmomo/initiate`, paymentData);
      
      if (paymentResponse?.status === 200) {
        toast.success("Paiement réussi!");
        
        // Enregistrer le plan personnalisé si nécessaire
        if (showCustomPlan) {
          try {
            // Préparer les données du plan personnalisé selon le schéma
            const personalizedPlanData = {
              hospitalId: userData.main_hospital_id,
              features: customPlan.features.filter(plan => plan.quantity > 0),
              planDuration: customPlan.planDuration,
              planPrice: calculateTotalPrice(),
              isPaid: true // Marquer comme payé
            };
            
            // Créer le plan personnalisé dans la base de données
            const newPlanResponse = await api.post('/api/customize-subscription', personalizedPlanData);
            
            // Mettre à jour l'abonnement de l'hôpital avec le nouveau plan personnalisé
            // if (newPlanResponse.data && newPlanResponse.data._id) {
            //   await api.put(`/api/updatehospital/${userData.hospital_id[0]._id}`, {
            //     subscription_id: newPlanResponse.data._id
            //   });
            // }
          } catch (error) {
            console.error('Erreur lors de la création du plan personnalisé:', error);
            toast.error('Erreur lors de la création du plan personnalisé');
            return;
          }
        } else {
          // Mettre à jour avec un plan existant
          await api.put(`/api/updatehospital/${userData.hospital_id[0]._id}`, {
            subscription_id: selectedPlan._id
          });
        }
        
        setTimeout(() => {
          window.location.href = '/hospitaladmin/dashboard';
        }, 1000);
      } else {
        toast.error("Échec du paiement");
      }
    } catch (error) {
      console.error('Erreur lors du paiement:', error);
      toast.error(`Échec du paiement: ${error?.message}`);
    }
  };

  const confirmPayment = (e) => {
    e.preventDefault();
    
    if (selectedPlan?._id === currentPlan?._id) {
      // Cas de renouvellement du même plan
      renewCurrentPlan();
      return;
    }
    
    if (!selectedPlan && !showCustomPlan) {
      toast.warning('Veuillez d\'abord sélectionner un plan');
      return;
    }
    
    if (paymentMethod === 'paypal') {
      handlePaypalPayment();
    } else if (paymentMethod === 'mtnmomo') {
      handleMomoPayment();
    } else {
      toast.warning('Veuillez sélectionner une méthode de paiement');
    }
  };

  const cancelUpdate = () => {
    setCustomPlan({
      hospitalSpecId: userData?.main_hospital_id || "",
      features: [],
      planDuration: 1,
      planPrice: 0,
      isPaid: false
    });
    setSelectedPlan(null);
    setCompareMode(false);
    setShowCustomPlan(false);
  };

  const handleShowCustomPlan = () => {
    // Initialiser le plan personnalisé avec les fonctionnalités disponibles
    const initialFeatures = features.map(feature => ({
      featureId: feature._id,
      quantity: 0
    }));
    
    setCustomPlan({
      ...customPlan,
      features: initialFeatures,
      planDuration: 1,
      planPrice: 0
    });
    
    setShowCustomPlan(true);
    setCompareMode(true);
    setSelectedPlan(null);
  };

  const handleCustomPlanChange = (e) => {
    const { name, value } = e.target;
    setCustomPlan({
      ...customPlan,
      [name]: value
    });
  };

  const handleCustomFeatureChange = (featureId, quantity) => {
    setCustomPlan(prevPlan => {
      const updatedFeatures = prevPlan.features.map(feature => {
        if (feature.featureId === featureId) {
          return { ...feature, quantity: parseInt(quantity) };
        }
        return feature;
      });
      
      const newPlan = { ...prevPlan, features: updatedFeatures };
      // Mettre à jour le prix automatiquement
      newPlan.planPrice = calculateTotalPrice();
      
      return newPlan;
    });
  };

  // Gestionnaire pour le changement de durée
  const handleDurationChange = (e) => {
    const durationInMonths = parseInt(e.target.value);
    // Convertir la durée en jours (1 mois = 30 jours)
    const durationInDays = durationInMonths;
    
    setCustomPlan(prevPlan => {
      const newPlan = {
        ...prevPlan,
        planDuration: durationInDays
      };
      // Recalculer le prix avec la nouvelle durée
      newPlan.planPrice = calculateTotalPrice();
      
      return newPlan;
    });
  };

  const isFeatureAvailable = (feature) => {
    if (!currentPlan || !currentPlan.features || !Array.isArray(currentPlan.features)) {
      return false;
    }
    
    // Try to find the feature in the current plan
    const matchingFeature = currentPlan.features.find(planFeature => {
      // Match by ID if possible
      if (feature._id) {
        return (
          (planFeature.feature && planFeature.feature._id === feature._id) ||
          (planFeature.featureId && planFeature.featureId._id === feature._id)
        );
      }
      
      // Match by name as fallback
      const featureName = feature.name || 
                         (feature.feature ? feature.feature.name : '') || 
                         (feature.featureId ? feature.featureId.name : '');
      
      const planFeatureName = planFeature.feature?.name || planFeature.featureId?.name || '';
      
      return featureName === planFeatureName;
    });
    
    if (!matchingFeature) return false;
    
    // Check if the feature is available based on value or quantity
    if ('value' in matchingFeature) {
      return matchingFeature.value === true || matchingFeature.value === 'true';
    } else if ('quantity' in matchingFeature) {
      return matchingFeature.quantity > 0;
    }
    
    return false;
  };
  
  // Get feature name function
  const getFeatureName = (feature) => {
    return feature.name || 
           (feature.feature ? feature.feature.name : '') || 
           (feature.featureId ? feature.featureId.name : '');
  };

  if (loading) {
    return <div className="text-center py-5">Chargement en cours...</div>;
  }

  return (
    <div className="update-subscription-container">
      <ToastContainer />
      {isExpired && (
        <div className="alert alert-warning mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="alert-heading">Votre abonnement a expiré!</h4>
              <p>Date d'expiration: {expirationDate?.toLocaleDateString()}</p>
              <p>Veuillez renouveler votre abonnement pour continuer à utiliser tous les services.</p>
            </div>
            <div>
              <button 
                className="btn btn-warning me-2"
                onClick={() => {
                  setCompareMode(true);
                  setSelectedPlan(currentPlan);
                  setShowCustomPlan(false);
                  window.scrollTo(0, document.body.scrollHeight);
                }}
              >
                Renouveler mon plan
              </button>
            </div>
          </div>
        </div>
      )}
      {/* {!isExpired && expirationDate && (
        <div className="alert alert-info mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="alert-heading">Information sur votre abonnement</h4>
              <p>Votre abonnement expire le: {expirationDate?.toLocaleDateString()}</p>
              <p>Vous pouvez renouveler votre abonnement avant son expiration pour éviter toute interruption de service.</p>
            </div>
            <div>
              <button 
                className="btn btn-outline-primary me-2"
                onClick={() => {
                  setCompareMode(true);
                  setSelectedPlan(currentPlan);
                  setShowCustomPlan(false);
                  window.scrollTo(0, document.body.scrollHeight);
                }}
              >
                Renouveler par anticipation
              </button>
            </div>
          </div>
        </div>
      )} */}
      <motion.div
        initial={{ x: '-100vw' }}
        animate={{ x: 0 }}
        exit={{ x: '100vw' }}
        transition={{ duration: 0.5 }}
      >
        <div className='microbes'>
          <div className='coule'>
            <img src={medic} alt="Medic" className="medical mb-3 mt-2" />
            <div className='container ones'>
              <div className='row'>
                <div className='col-lg-3'>
                  <h1 className='prunel'><span className='fou1'>Med</span><span className='fou2'>Tinz</span></h1>
                </div>
                <div className='col-lg-12'>
                  <div className='canas'>
                    <h2 className='claudia text-center'>
                    Update your<br />
                    subscription plan
                    </h2>
                    <p className='riches'>
                    Choose the plan that best suits the needs of your medical facility. You can opt for a standard plan or create a custom plan based on your specific needs.
                    </p>
                  </div>
                </div>
                <div className='col-lg-3'></div>
              </div>
            </div>
          </div>

          <div className='container mt-4'>
            {!compareMode ? (
              <div className='row'>
                <div className='col-12'>
                  <h2>Your current plan</h2>
                  <div className='current-plan-card p-4 border rounded mb-4'>
                    <h3>{currentPlan?.name || "Personnalize plan"}</h3>
                    <h4>{currentPlan?.price || currentPlan?.planPrice} FCFA <span>per {(currentPlan?.planDuration || currentPlan?.duration) < 12 ? `${(currentPlan?.planDuration || currentPlan?.duration)} month` : 'year'}</span></h4>
                    <div className="mt-3">
                      <h5>Features included:</h5>
                      <ul className="list-group">
                      {features.map((feature, index) => {
                        const available = isFeatureAvailable(feature);
                        const featureName = getFeatureName(feature);
                        
                        return (
                          <li key={index} className="list-group-item d-flex" style={{ border: "none" }}>
                            <span>
                              {available ? (
                                <span style={{ color: "green", fontWeight: "bold" }}>✅</span>
                              ) : (
                                <span style={{ color: "red", fontWeight: "bold" }}>❌</span>
                              )}
                            </span>
                            <span className="mx-2">{featureName}</span>
                          </li>
                        );
                      })}
                      </ul>
                    </div>
                  </div>
                  
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h2>Plans disponibles</h2>
                    <button 
                      className="btn btn-outline-primary"
                      onClick={handleShowCustomPlan}
                    >
                      Create a custom plan
                    </button>
                  </div>
                  
                  <div className='row'>
                    {plans.map(plan => (
                      <div key={plan._id} className='col-md-4 mb-4'>
                        <div className={`plan-card p-4 border rounded ${plan._id === currentPlan?._id ? 'bg-light' : ''}`}>
                          <h3>{plan.name}</h3>
                          <h4>{plan.price} FCFA <span>par {plan.duration < 12 ? `${plan.duration} mois` : 'an'}</span></h4>
                          {plan._id === currentPlan?._id ? (
                            <button className='btn btn-secondary mt-3' disabled>Current plan</button>
                          ) : (
                            <button 
                              className='btn btn-primary mt-3'
                              onClick={() => handleSelectPlan(plan)}
                            >
                              Choose this plan
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : showCustomPlan ? (
              // Interface de création de plan personnalisé
              <div className="row">
                <div className="col-lg-6">
                  <h3>Create a custom plan</h3>
                  <div className="custom-plan-form p-4 border rounded">
                    
                    <div className="form-group mb-3">
                      <label htmlFor="price">Price (FCFA)</label>
                      <input
                        type="number"
                        className="form-control"
                        id="price"
                        name="price"
                        value={calculateTotalPrice()}
                        disabled
                        readOnly
                      />
                      <small className="text-muted">The price is calculated based on the features selected</small>
                    </div>
                    
                    <div className="form-group mb-3">
                      <label htmlFor="duration">Duration</label>
                      <select
                        className="form-control"
                        id="duration"
                        value={customPlan.planDuration}
                        onChange={handleDurationChange}
                      >
                        <option value="1">1 month</option>
                        <option value="3">3 months</option>
                        <option value="6">6 months</option>
                        <option value="12">1 year</option>
                      </select>
                    </div>
                    
                    <h4 className="mt-4">Select your features</h4>
                    <div className="features-selection">
                      {features.map((feature, index) => (
                        <div key={index} className="form-group mb-2">
                          <div className="d-flex align-items-center">
                            <label className="me-3">
                              {feature.name} 
                              <span className="ms-2 text-muted">({feature.price} FCFA)</span>
                            </label>
                            <select
                              className="form-control"
                              value={customPlan.features.find(f => f.featureId === feature._id)?.quantity || 0}
                              onChange={(e) => handleCustomFeatureChange(feature._id, e.target.value)}
                            >
                              <option value="0">Not included</option>
                              <option value="1">Included</option>
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="action-buttons mt-4">
                      <button 
                        className="btn btn-secondary me-2"
                        onClick={cancelUpdate}
                      >
                        Cancel
                      </button>
                      <button 
                        className="btn btn-primary"
                        onClick={() => window.scrollTo(0, document.body.scrollHeight)}
                      >
                        Proceed to payment
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="col-lg-6">
                  <h3>Comparison of plans</h3>
                  <div className="comparison-container p-4 border rounded">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Functionality</th>
                          <th>Current plan: {currentPlan?.name || "Plan personnalisé"}</th>
                          <th>Personalized plan: {customPlan.name}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Price</td>
                          <td>{currentPlan?.price || currentPlan?.planPrice} FCFA</td>
                          <td>{calculateTotalPrice()} FCFA</td>
                        </tr>
                        <tr>
                          <td>Duration</td>
                          <td>{(currentPlan?.planDuration || currentPlan?.duration) < 12 ? `${(currentPlan?.planDuration || currentPlan?.duration)} month` : '1 year'}</td>
                          <td>{customPlan.planDuration < 12 ? `${customPlan.planDuration} month` : '1 year'}</td>
                        </tr>
                        {features.map((feature, index) => {
                        const available = isFeatureAvailable(feature);
                        const featureName = getFeatureName(feature);
                        
                          // Get the customFeatureValue directly as in original code
                          const customFeatureValue = customPlan?.features?.[index];
                          
                          return (
                            <tr key={index}>
                              <td>{featureName} <span className="text-muted">({feature.price} FCFA)</span></td>
                              <td>
                              <li key={index} className="list-group-item d-flex" style={{ border: "none" }}>
                                <span>
                                  {available ? (
                                    <span style={{ color: "green", fontWeight: "bold" }}>✅</span>
                                  ) : (
                                    <span style={{ color: "red", fontWeight: "bold" }}>❌</span>
                                  )}
                                </span>
                              </li>
                              </td>
                              <td>
                                {customFeatureValue?.quantity > 0 ? (
                                  <span style={{ color: "green", fontWeight: "bold" }}>✅</span>
                                ) : customFeatureValue?.quantity <= 0  === 'false' ? (
                                  <span style={{ color: "red", fontWeight: "bold" }}>❌</span>
                                ) : customFeatureValue?.value !== undefined && customFeatureValue?.value !== null ? (
                                  <span>{customFeatureValue?.value}</span>
                                ) : (
                                  <span>-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  <h3 className="mt-4">Payment Method</h3>
                  <div className="payment-methods d-flex flex-column p-4 border rounded">
                    <div className="payment-options d-flex">
                      <div className="payment-option mb-3 me-3">
                        <input
                          type="radio"
                          id="paypal"
                          name="paymentMethod"
                          value="paypal"
                          checked={paymentMethod === 'paypal'}
                          onChange={handleSelectPaymentMethod}
                        />
                        <label htmlFor="paypal" className="ms-2">
                          <img src={paypal} alt="PayPal" style={{ height: '30px' }} />
                        </label>
                      </div>
                      
                      <div className="payment-option mb-3 me-3">
                        <input
                          type="radio"
                          id="mtnmomo"
                          name="paymentMethod"
                          value="mtnmomo"
                          checked={paymentMethod === 'mtnmomo'}
                          onChange={handleSelectPaymentMethod}
                        />
                        <label htmlFor="mtnmomo" className="ms-2">
                          <img src={mtn} alt="MTN MoMo" style={{ height: '30px' }} />
                        </label>
                      </div>
                      
                      <div className="payment-option mb-3">
                        <input
                          type="radio"
                          id="card"
                          name="paymentMethod"
                          value="card"
                          checked={paymentMethod === 'card'}
                          onChange={handleSelectPaymentMethod}
                        />
                        <label htmlFor="card" className="ms-2">
                          <img src={bank} alt="Card" style={{ height: '30px' }} />
                        </label>
                      </div>
                    </div>
                    
                    {paymentMethod === 'mtnmomo' && (
                      <div className="phone-input mt-3">
                        <label htmlFor="phoneNumber">Phone number:</label>
                        <input
                          type="tel"
                          id="phoneNumber"
                          className="form-control"
                          value={phoneNumber}
                          onChange={handlePhoneNumberChange}
                          placeholder="Entrez votre numéro de téléphone"
                        />
                      </div>
                    )}
                    
                    <button
                      className="btn btn-success mt-4 w-100"
                      onClick={confirmPayment}
                      disabled={!paymentMethod || (paymentMethod === 'mtnmomo' && !phoneNumber)}
                    >
                      Confirm payment
                    </button>
                  </div>
                  
                  <div className="price-difference alert alert-info mt-3">
                    <h5>Amount to pay: {calculatePriceDifference()} FCFA</h5>
                    <p>This amount corresponds to the price of your personalized plan.</p>
                  </div>
                </div>
              </div>
            ) : (
              // Interface de comparaison avec un plan existant
              <div className='row'>
                <div className='col-lg-6'>
                  <h3>Comparison of plans</h3>
                  <div className='comparison-container p-4 border rounded'>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>feature</th>
                          <th>Current plan: {currentPlan?.name || "plan personnalisé"}</th>
                          <th>New plan: {selectedPlan?.name}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Price</td>
                          <td>{currentPlan?.price || currentPlan?.planPrice} FCFA</td>
                          <td>{selectedPlan?.price} FCFA</td>
                        </tr>
                        <tr>
                          <td>Duration</td>
                          <td>{(currentPlan?.planDuration || currentPlan?.duration) < 12 ? `${(currentPlan?.planDuration || currentPlan?.duration)} mois` : '1 an'}</td>
                          <td>{selectedPlan?.duration < 12 ? `${selectedPlan?.duration} mois` : '1 an'}</td>
                        </tr>
                        {features.map((feature, index) => {
                          // Helper function to determine feature availability and value
                          const getFeatureStatus = (plan, featureIndex) => {
                            if (!plan?.features || !Array.isArray(plan.features)) {
                              return { isAvailable: false, displayValue: "-" };
                            }
                            
                            const planFeature = plan.features[featureIndex];
                            
                            // Format 1: From paste.txt (features with feature, type, value properties)
                            if (planFeature?.feature && planFeature?.value !== undefined) {
                              if (planFeature.value === "true" || planFeature.value === true) {
                                return { isAvailable: true, displayValue: "✅" };
                              } else if (planFeature.value === "false" || planFeature.value === false) {
                                return { isAvailable: false, displayValue: "❌" };
                              } else {
                                return { isAvailable: true, displayValue: planFeature.value };
                              }
                            }
                            // Format 2: From paste-2.txt (features with featureId, quantity properties)
                            else if (planFeature?.featureId && planFeature?.quantity !== undefined) {
                              if (planFeature.quantity > 0) {
                                return { isAvailable: true, displayValue: "✅" };
                              } else {
                                return { isAvailable: false, displayValue: "❌" };
                              }
                            }
                            
                            // Try to find matching feature by ID for more complex cases
                            const featureId = feature._id || feature.feature?._id || feature.featureId?._id;
                            if (featureId) {
                              const match = plan.features.find(f => 
                                (f.feature?._id === featureId) || 
                                (f.featureId?._id === featureId)
                              );
                              
                              if (match) {
                                if (match.value === "true" || match.value === true || match.quantity > 0) {
                                  return { isAvailable: true, displayValue: "✅" };
                                } else if (match.value === "false" || match.value === false || match.quantity === 0) {
                                  return { isAvailable: false, displayValue: "❌" };
                                } else if (match.value !== undefined) {
                                  return { isAvailable: true, displayValue: match.value };
                                }
                              }
                            }
                            
                            return { isAvailable: false, displayValue: "-" };
                          };
                          
                          // Get feature name regardless of structure
                          const featureName = feature.name || 
                                            (feature.feature ? feature.feature.name : "") || 
                                            (feature.featureId ? feature.featureId.name : "");
                          
                          // Get status for current and selected plans
                          const currentStatus = getFeatureStatus(currentPlan, index);
                          const newStatus = getFeatureStatus(selectedPlan, index);
                          
                          return (
                            <tr key={index}>
                              <td>{featureName}</td>
                              <td>
                                {currentStatus.isAvailable ? (
                                  <span style={{ color: "green", fontWeight: "bold" }}>{currentStatus.displayValue}</span>
                                ) : currentStatus.displayValue === "❌" ? (
                                  <span style={{ color: "red", fontWeight: "bold" }}>{currentStatus.displayValue}</span>
                                ) : (
                                  <span>{currentStatus.displayValue}</span>
                                )}
                              </td>
                              <td>
                                {newStatus.isAvailable ? (
                                  <span style={{ color: "green", fontWeight: "bold" }}>{newStatus.displayValue}</span>
                                ) : newStatus.displayValue === "❌" ? (
                                  <span style={{ color: "red", fontWeight: "bold" }}>{newStatus.displayValue}</span>
                                ) : (
                                  <span>{newStatus.displayValue}</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    
                    <div className="action-buttons mt-3">
                      <button 
                        className="btn btn-secondary me-2"
                        onClick={cancelUpdate}
                      >
                        Cancel
                      </button>
                      <button 
                        className="btn btn-primary"
                        onClick={() => window.scrollTo(0, document.body.scrollHeight)}
                      >
                        Proceed to payment
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className='col-lg-6'>
                  <h3>Méthode de paiement</h3>
                  <div className="payment-methods d-flex flex-column p-4 border rounded">
                    <div className="payment-options d-flex">
                      <div className="payment-option mb-3 me-3">
                        <input
                          type="radio"
                          id="paypal"
                          name="paymentMethod"
                          value="paypal"
                          checked={paymentMethod === 'paypal'}
                          onChange={handleSelectPaymentMethod}
                        />
                        <label htmlFor="paypal" className="ms-2">
                          <img src={paypal} alt="PayPal" style={{ height: '30px' }} />
                        </label>
                      </div>
                      
                      <div className="payment-option mb-3 me-3">
                        <input
                          type="radio"
                          id="mtnmomo"
                          name="paymentMethod"
                          value="mtnmomo"
                          checked={paymentMethod === 'mtnmomo'}
                          onChange={handleSelectPaymentMethod}
                        />
                        <label htmlFor="mtnmomo" className="ms-2">
                          <img src={mtn} alt="MTN MoMo" style={{ height: '30px' }} />
                        </label>
                      </div>
                      
                      <div className="payment-option mb-3">
                        <input
                          type="radio"
                          id="card"
                          name="paymentMethod"
                          value="card"
                          checked={paymentMethod === 'card'}
                          onChange={handleSelectPaymentMethod}
                        />
                        <label htmlFor="card" className="ms-2">
                          <img src={bank} alt="Card" style={{ height: '30px' }} />
                        </label>
                      </div>
                    </div>
                    
                    {paymentMethod === 'mtnmomo' && (
                      <div className="phone-input mt-3">
                        <label htmlFor="phoneNumber">Phone number:</label>
                        <input
                          type="tel"
                          id="phoneNumber"
                          className="form-control"
                          value={phoneNumber}
                          onChange={handlePhoneNumberChange}
                          placeholder="Entrez votre numéro de téléphone"
                        />
                      </div>
                    )}
                    
                    <button
                      className="btn btn-success mt-4 w-100"
                      onClick={confirmPayment}
                      disabled={!paymentMethod || (paymentMethod === 'mtnmomo' && !phoneNumber)}
                    >
                      Confirm payment
                    </button>
                  </div>
                  
                  <div className="price-difference alert alert-info mt-3">
                    <h5>Amount to pay: {calculatePriceDifference()} FCFA</h5>
                    <p>This amount corresponds to the difference between your current plan and the new plan chosen.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default UpdateSubscription;